import type { DelayedUnstakeNoticeBodyProps } from "./DelayedUnstakeNoticeBody";
import { DelayedUnstakeNoticeBody } from "./DelayedUnstakeNoticeBody";
import type { DelegationBodyProps } from "./DelegationBody";
import { DelegationBody } from "./DelegationBody";
import { DirectRequestBody } from "./DirectRequestBody";
import type { DirectRequestBodyProps } from "./DirectRequestBody";
import type { IconTitleProps } from "./IconTitle";
import { IconTitle } from "./IconTitle";
import type { InformationBodyProps } from "./InformationBody";
import { InformationBody } from "./InformationBody";
import type { OnboardingBodyProps } from "./OnboardingBody";
import { OnboardingBody } from "./OnboardingBody";
import type { ProcessTransactionBodyProps } from "./ProcessTransactionBody";
import { ProcessTransactionBody } from "./ProcessTransactionBody";
import { PromoBody, type PromoBodyProps } from "./PromoBody";
import type { SimpleTitleProps } from "./SimpleTitle";
import { SimpleTitle } from "./SimpleTitle";
import type { StakeMndeBodyProps } from "./StakeMndeBody";
import { StakeMndeBody } from "./StakeMndeBody";
import { StSOLDeprecatedBody } from "./StSOLDeprecatedBody";
import type { StSOLDeprecatedBodyProps } from "./StSOLDeprecatedBody";
import type { UnstakeMethodSelectionBodyProps } from "./UnstakeMethodSelectionBody";
import { UnstakeMethodSelectionBody } from "./UnstakeMethodSelectionBody";
import type { ValidatorSelectionBodyProps } from "./ValidatorSelectionBody";
import { ValidatorSelectionBody } from "./ValidatorSelectionBody";
import type { WalletBodyProps } from "./WalletBody";
import { WalletBody } from "./WalletBody";
import type { WalletConnectBodyProps } from "./WalletConnectBody";
import { WalletConnectBody } from "./WalletConnectBody";

export interface WalletTitleProps {
  title: string;
}

export interface CombinedTitleProps
  extends IconTitleProps,
    WalletTitleProps,
    SimpleTitleProps {}

export type TitleProps = Partial<CombinedTitleProps>;

export const injectTitleNode = (props?: TitleProps) => {
  if (props?.icon !== undefined) {
    return <IconTitle {...(props as IconTitleProps)} />;
  }
  if (props?.title === "Wallet") {
    return (
      <div className="text-left pr-10 md:text-24 md:leading-30 md:font-semibold md:tracking-tight text-20 leading-26">
        {props.title}
      </div>
    );
  }

  if (props?.title) {
    return <SimpleTitle {...(props as SimpleTitleProps)} />;
  }

  return null;
};

interface WalletProps extends WalletBodyProps {
  type: "wallet";
}

export interface CombinedBodyProps
  extends WalletProps,
    WalletConnectBodyProps,
    DelayedUnstakeNoticeBodyProps,
    InformationBodyProps,
    DelegationBodyProps,
    StakeMndeBodyProps,
    PromoBodyProps,
    DirectRequestBodyProps,
    UnstakeMethodSelectionBodyProps,
    ProcessTransactionBodyProps,
    OnboardingBodyProps,
    ValidatorSelectionBodyProps,
    StSOLDeprecatedBodyProps {}

export type BodyProps = Partial<CombinedBodyProps>;

export const injectBodyNode = (props?: BodyProps) => {
  if (props?.type === "wallet") {
    return <WalletBody {...(props as WalletBodyProps)} />;
  }
  if (props?.requestConnection) {
    return <WalletConnectBody {...(props as WalletConnectBodyProps)} />;
  }
  if (props?.runStakeMnde) {
    return <StakeMndeBody {...(props as StakeMndeBodyProps)} />;
  }
  if (props?.requestDirectingVoteAccount) {
    return <DirectRequestBody {...(props as DirectRequestBodyProps)} />;
  }
  if (props?.txType !== undefined) {
    return (
      <ProcessTransactionBody {...(props as ProcessTransactionBodyProps)} />
    );
  }
  if (props?.informationText) {
    return <InformationBody {...(props as InformationBodyProps)} />;
  }

  if (props?.strategies) {
    return <DelegationBody {...(props as DelegationBodyProps)} />;
  }
  if (props?.runUnstake) {
    return (
      <DelayedUnstakeNoticeBody {...(props as DelayedUnstakeNoticeBodyProps)} />
    );
  }
  if (props?.onValidatorSelection) {
    return (
      <ValidatorSelectionBody {...(props as ValidatorSelectionBodyProps)} />
    );
  }
  if (props?.steps) {
    return <OnboardingBody {...(props as OnboardingBodyProps)} />;
  }

  if (props?.stSOLApy) {
    return <StSOLDeprecatedBody {...(props as StSOLDeprecatedBodyProps)} />;
  }

  if (props?.promoTitle) {
    return <PromoBody {...(props as PromoBodyProps)} />;
  }

  if (props?.onUnstakeMethodChange) {
    return (
      <UnstakeMethodSelectionBody
        {...(props as UnstakeMethodSelectionBodyProps)}
      />
    );
  }

  return null;
};
